/**
 * §A.2 · Los planes de Kiap: qué vendemos.
 *
 * ## Editar el precio de un plan **no le sube la cuota a nadie**
 *
 * Es lo primero que hay que tener claro al entrar aquí, y el contrato lo dice: la
 * suscripción de cada club guarda **su** precio real. Eso es lo que hace posible el
 * precio fundador de 19 € de por vida sin inventarse un plan por cliente, y lo que
 * evita refacturar a doscientos clubes por editar una fila.
 *
 * Quien cambia un precio aquí está pensando «qué cobramos a partir de ahora», y si la
 * pantalla no lo dice va a creer que acaba de subirle la cuota a todo el mundo —o
 * peor: no lo va a cambiar, por miedo—. Así que se dice donde se edita.
 *
 * ## `max_students: null` es **ilimitado**, no cero
 *
 * Lo pone el DTO con esas palabras. Un campo vacío que se lee como «cero alumnos»
 * convertiría el plan Academia en el plan más pequeño de los tres.
 *
 * ## El `code` no se edita
 *
 * Es lo que enlaza el plan con lo ya contratado. Se enseña como dato y sin campo,
 * porque un campo deshabilitado invita a preguntar por qué.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta, euros } from '@/shared/ui/formato';
import { aCentimos, aEuros } from '@/club/cuotas/tipos';
import {
  FUNCIONES_BASE,
  FUNCIONES_DE_PLAN,
  funcionesDe,
  type Plan,
} from '@/backoffice/tipos';

/** Lo que se teclea de un plan. El `code` solo al crear. */
interface Borrador {
  code: string;
  name: string;
  precio: string;
  maxAlumnos: string;
  maxSedes: string;
  publico: boolean;
  /** Las etiquetas de `FUNCIONES_DE_PLAN` que incluye. */
  funciones: readonly string[];
}

const VACIO: Borrador = {
  code: '',
  name: '',
  precio: '',
  maxAlumnos: '',
  maxSedes: '',
  publico: true,
  funciones: [],
};

const deUnPlan = (p: Plan): Borrador => ({
  code: p.code,
  name: p.name,
  precio: aEuros(p.price_cents),
  // Vacío es ilimitado, que es lo que significa `null`.
  maxAlumnos: p.max_students === null ? '' : String(p.max_students),
  maxSedes: p.max_venues === null ? '' : String(p.max_venues),
  publico: p.is_public,
  funciones: funcionesDe(p.features),
});

/**
 * El `features` que se guarda.
 *
 * Parte del que tenía el plan para **no perder claves que no son funciones** —el
 * Fundador lleva una `nota`—, pone la base siempre y escribe cada función con su
 * `true` o `false`: un `false` no abre nada y deja claro que se decidió quitarla.
 */
function aFeatures(
  anterior: Record<string, unknown>,
  funciones: readonly string[]
): Record<string, unknown> {
  return {
    ...anterior,
    ...Object.fromEntries(FUNCIONES_BASE.map((f) => [f, true])),
    ...Object.fromEntries(
      FUNCIONES_DE_PLAN.map((f) => [f.etiqueta, funciones.includes(f.etiqueta)])
    ),
  };
}

/** Texto → tope, donde **vacío es ilimitado** y no cero. */
function aTope(texto: string): number | null | undefined {
  const limpio = texto.trim();
  if (limpio === '') return null;
  const n = Number(limpio);
  if (!Number.isInteger(n) || n < 1) return undefined;
  return n;
}

export function Planes() {
  const cache = useQueryClient();
  const [editando, setEditando] = useState<Plan | null>(null);
  const [creando, setCreando] = useState(false);
  const [borrador, setBorrador] = useState<Borrador>(VACIO);
  const [problema, setProblema] = useState<Problema | null>(null);

  const planes = useQuery({
    queryKey: ['admin', 'planes'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin.plans.get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Plan[];
    },
  });

  const guardar = useMutation({
    mutationFn: async () => {
      const centimos = aCentimos(borrador.precio);
      const alumnos = aTope(borrador.maxAlumnos);
      const sedes = aTope(borrador.maxSedes);

      if (centimos === null || alumnos === undefined || sedes === undefined) {
        throw new ErrorDeApi({
          title: 'Hay algo que no se entiende',
          status: 400,
          detail:
            'El precio va en euros con dos decimales como mucho —29 o 29,50—, y los topes son números enteros de 1 en adelante. Déjalos vacíos para «ilimitado».',
        });
      }

      const comun = {
        name: borrador.name.trim(),
        price_cents: centimos,
        max_students: alumnos,
        max_venues: sedes,
        is_public: borrador.publico,
        features: aFeatures(editando?.features ?? {}, borrador.funciones),
      };

      if (editando) {
        // Sin `code`: es lo que enlaza el plan con lo contratado y no se edita.
        const r = await api.v1.admin.plans({ id: editando.id }).patch(comun);
        if (r.error) throw new ErrorDeApi(aProblema(r.error));
        return r.data;
      }

      const r = await api.v1.admin.plans.post({
        ...comun,
        code: borrador.code.trim(),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setEditando(null);
      setCreando(false);
      setBorrador(VACIO);
      void cache.invalidateQueries({ queryKey: ['admin', 'planes'] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  // ── El formulario ────────────────────────────────────────────────────────
  if (creando || editando) {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          guardar.mutate();
        }}
      >
        <div className="titulo-fila">
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setCreando(false);
              setEditando(null);
              setProblema(null);
            }}
          >
            ← Planes
          </button>
        </div>

        <h1>{editando ? `Editar «${editando.name}»` : 'Un plan nuevo'}</h1>

        {editando ? (
          <div className="aviso" role="status">
            <p>
              <strong>Cambiar el precio no le sube la cuota a nadie.</strong>{' '}
              Cada club guarda el precio con el que entró, así que esto es lo
              que se cobrará a partir de ahora, a quien se dé de alta desde ya.
            </p>
            <p className="tenue">
              Es lo que hace posible el precio fundador sin inventarse un plan
              por cliente. Para cambiarle el precio a un club concreto, se hace
              en su ficha.
            </p>
          </div>
        ) : (
          <p className="tenue">
            El código es lo que enlaza el plan con lo contratado y luego no se
            puede cambiar: en minúsculas y con guiones bajos, como{' '}
            <code>club_pro</code>.
          </p>
        )}

        <div className="rejilla">
          {!editando && (
            <label>
              Código
              <input
                required
                maxLength={40}
                pattern="[a-z0-9_]+"
                placeholder="club_pro"
                value={borrador.code}
                onChange={(e) =>
                  setBorrador({ ...borrador, code: e.target.value })
                }
              />
            </label>
          )}

          <label>
            Cómo se llama
            <input
              required
              maxLength={80}
              placeholder="Club Pro"
              value={borrador.name}
              onChange={(e) =>
                setBorrador({ ...borrador, name: e.target.value })
              }
            />
          </label>

          <label>
            Al mes
            <input
              required
              inputMode="decimal"
              placeholder="49,00"
              value={borrador.precio}
              onChange={(e) =>
                setBorrador({ ...borrador, precio: e.target.value })
              }
            />
          </label>

          <label>
            Tope de alumnos
            <input
              inputMode="numeric"
              placeholder="Sin tope"
              value={borrador.maxAlumnos}
              onChange={(e) =>
                setBorrador({ ...borrador, maxAlumnos: e.target.value })
              }
            />
          </label>

          <label>
            Tope de sedes
            <input
              inputMode="numeric"
              placeholder="Sin tope"
              value={borrador.maxSedes}
              onChange={(e) =>
                setBorrador({ ...borrador, maxSedes: e.target.value })
              }
            />
          </label>

          <label>
            Se ofrece
            <select
              value={borrador.publico ? 'si' : 'no'}
              onChange={(e) =>
                setBorrador({ ...borrador, publico: e.target.value === 'si' })
              }
            >
              <option value="si">Sí, está en el catálogo</option>
              <option value="no">No, solo a mano</option>
            </select>
          </label>
        </div>

        <fieldset className="funciones-de-plan">
          <legend>Qué incluye</legend>
          <p className="tenue">
            Panel, app del maestro y app de familias van en todos. Lo que
            marques aquí se abre o se cierra <strong>al momento</strong> a todos
            los clubes con este plan, y el mensaje de «viene con el plan…» lo
            sigue solo. Quitar una función no borra lo ya creado: el club lo
            conserva, pero no puede crear más.
          </p>
          <ul className="roles">
            {FUNCIONES_DE_PLAN.map((f) => (
              <li key={f.etiqueta}>
                <label className="opcion">
                  <input
                    type="checkbox"
                    checked={borrador.funciones.includes(f.etiqueta)}
                    onChange={(e) =>
                      setBorrador({
                        ...borrador,
                        funciones: e.target.checked
                          ? [...borrador.funciones, f.etiqueta]
                          : borrador.funciones.filter((x) => x !== f.etiqueta),
                      })
                    }
                  />
                  <span>
                    <strong>{f.nombre}</strong>
                    <em>{f.explicacion}</em>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>

        <p className="tenue">
          {/* «Vacío es ilimitado» hay que decirlo: un hueco se lee como cero. */}
          Los topes vacíos son <strong>ilimitado</strong>, no cero. Un plan
          privado sigue funcionando para quien ya lo tiene; solo deja de
          ofrecerse.
        </p>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={guardar.isPending}>
            {guardar.isPending
              ? 'Guardando…'
              : editando
                ? 'Guardar los cambios'
                : 'Crear el plan'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={guardar.isPending}
            onClick={() => {
              setCreando(false);
              setEditando(null);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── La lista ─────────────────────────────────────────────────────────────
  if (planes.isPending)
    return <Cargando filas={4} etiqueta="Cargando los planes" />;

  if (planes.isError || !planes.data) {
    return (
      <PanelError
        problema={aProblema(planes.error)}
        reintentar={() => void planes.refetch()}
      />
    );
  }

  return (
    <section>
      <div className="titulo-fila">
        <h1>Planes</h1>
        <button
          type="button"
          onClick={() => {
            setBorrador(VACIO);
            setCreando(true);
          }}
        >
          Un plan nuevo
        </button>
      </div>

      <p className="tenue">
        Lo que vendemos. Cambiar un precio de aquí no toca lo que ya paga nadie:
        cada club guarda el suyo.
      </p>

      {problema && <PanelError problema={problema} />}

      {planes.data.length === 0 ? (
        <Vacio
          titulo="No hay ningún plan"
          explicacion="Sin planes no se le puede dar de alta una suscripción a ningún club. Los tres de siempre son Club, Club Pro y Academia."
        />
      ) : (
        <ul className="papeles">
          {planes.data.map((p) => (
            <li key={p.id}>
              <div className="aviso-cabeza">
                <strong>{p.name}</strong>
                <span className="pastilla pastilla-left">
                  {euros(p.price_cents)} al mes
                </span>
                {!p.is_public && (
                  <span className="pastilla pastilla-paused">No se ofrece</span>
                )}
                <code className="tenue">{p.code}</code>
              </div>

              <p>
                {/* `null` es ilimitado y se escribe con esa palabra. */}
                {p.max_students === null
                  ? 'Alumnos sin tope'
                  : `Hasta ${cuenta(p.max_students, 'alumno', 'alumnos')}`}
                {' · '}
                {p.max_venues === null
                  ? 'sedes sin tope'
                  : `hasta ${cuenta(p.max_venues, 'sede', 'sedes')}`}
              </p>
              <p className="tenue">
                {(() => {
                  const suyas = FUNCIONES_DE_PLAN.filter((f) =>
                    funcionesDe(p.features).includes(f.etiqueta)
                  ).map((f) => f.nombre);
                  return suyas.length === 0
                    ? 'Solo la base: panel y apps.'
                    : `Además de la base: ${suyas.join(', ')}.`;
                })()}
              </p>

              <div className="aviso-pie">
                <span className="tenue">
                  {/*
                    Cuántos lo tienen es lo que dice si quitarlo del catálogo deja
                    a alguien colgado. Por eso va aquí y no escondido.
                  */}
                  {p.subscriptions === 0
                    ? 'Ningún club lo tiene contratado'
                    : `${cuenta(p.subscriptions, 'club', 'clubes')} lo ${
                        p.subscriptions === 1 ? 'tiene' : 'tienen'
                      } contratado`}
                </span>
                <button
                  type="button"
                  className="btn-plano"
                  onClick={() => {
                    setBorrador(deUnPlan(p));
                    setEditando(p);
                    setProblema(null);
                  }}
                >
                  Editarlo
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
